import {
    AuthContext,
    BaseButton,
    ClaimIdBadge,
    DataTable,
    DataTablePresenter,
    DocumentDetail,
    FilterMethod,
    ICustomFilter,
    IAuthContextProps,
    IFilterSchema,
    ITableColumn,
    ITableFetchOptions,
    ListProviderDocumentsRequest,
    ProviderCreateDocumentType,
    ProviderDisplayDocumentType,
    ProviderDocument,
    ProviderDocumentSortType,
    renderDate,
    renderEmpty,
    renderString,
} from '@enter/core';
import _ from 'lodash';
import { mdiUpload } from '@mdi/js';
import { DataTableCard, DocumentUploadDialog, PageHeader, PageLayout, PageLayoutRow } from 'components';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { Row } from 'react-table';
import { DialogContext, IDialogContextProps } from 'services';
import { ClaimStore, IInjectedRootStore, IObservedRootStore, parseSortBy } from 'stores';
import * as S from './documents.styles';

interface IDocumentsProps extends IInjectedRootStore {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
}

interface IDocumentsState {
    loading: boolean;
    options: ITableFetchOptions | undefined;
}

// Same order and labels as the support documents list. Support's Tags and Active columns are
// absent from `ProviderDocument`, so they are not offered here.
const columns: ITableColumn<ProviderDocument>[] = [
    {
        id: 'Id',
        Header: 'Id',
        accessor: 'id',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{renderString(cell.value)}</S.Cell>,
        show: false,
    },
    {
        id: 'DocumentType',
        Header: 'Document Type',
        accessor: 'documentType',
        disableSortBy: true,
        Cell: ({ cell }) => (
            <S.Cell>{renderString(cell.value, { type: 'String', normalized: true, formatted: true })}</S.Cell>
        ),
    },
    {
        id: 'Filename',
        Header: 'Filename',
        accessor: 'filename',
        disableSortBy: true,
        defaultSort: { id: 'DateAdded', desc: true },
        Cell: ({ cell }) => <S.Cell>{cell.value}</S.Cell>,
        minWidth: 250,
    },
    {
        id: 'Description',
        Header: 'Description',
        accessor: 'description',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{cell.value}</S.Cell>,
        show: false,
    },
    {
        id: 'Claim',
        Header: 'Claim',
        accessor: 'claimId',
        disableSortBy: true,
        minWidth: 200,
        Cell: ({ cell }) =>
            cell.value ? (
                // The badge is a link inside an expandable row — don't toggle the row as well.
                <S.Cell onClick={e => e.stopPropagation()}>
                    <ClaimIdBadge claimId={cell.value} />
                </S.Cell>
            ) : (
                <S.Cell>{renderEmpty()}</S.Cell>
            ),
        show: false,
    },
    {
        id: 'DateAdded',
        Header: 'Created Date',
        accessor: 'created',
        sortType: ProviderDocumentSortType.CREATED,
        Cell: ({ cell }) => <S.Cell>{renderDate(cell.value)}</S.Cell>,
        show: false,
    },
];

// The table's search box — the same document ID search as the support documents list. Accepts a
// comma-separated list of IDs.
export function documentsFilterParser(value: string): ICustomFilter[][] {
    return [
        value.split(',').map(id => ({
            filterType: 'String',
            filterId: 'filterId',
            value: id.trim() || undefined,
            filterMethod: FilterMethod.EQ,
        })),
    ];
}

const formatDocumentTypeLabel = (value: ProviderCreateDocumentType): string =>
    _.startCase(value.toLowerCase().replace(/_/g, ' '));

const documentTypeOptions = Object.values(ProviderCreateDocumentType).map(value => ({
    label: formatDocumentTypeLabel(value),
    value,
}));

const defaultDocumentTypes: ProviderCreateDocumentType[] = [
    ProviderCreateDocumentType.AIMAIL,
    ProviderCreateDocumentType.AIFAX,
    ProviderCreateDocumentType.AIPHONE,
    ProviderCreateDocumentType.AICLAIM,
];

const defaultDocumentFilters: ICustomFilter[] = defaultDocumentTypes.map(docType => ({
    filterId: 'filterDocumentType',
    filterMethod: FilterMethod.EQ,
    filterType: 'Select',
    value: {
        label: formatDocumentTypeLabel(docType),
        value: docType,
    },
}));

const filterSchemas: IFilterSchema<keyof ListProviderDocumentsRequest>[] = [
    {
        label: 'Claim ID',
        filterId: 'filterClaimId',
        columnId: 'claimId',
        filterType: 'String',
    },
    {
        label: 'Created',
        filterId: 'filterCreated',
        filterType: 'DateTime',
        columnId: 'Created',
    },
    {
        label: 'Document Type',
        filterId: 'filterDocumentType',
        columnId: 'documentType',
        filterType: 'Select',
        options: documentTypeOptions,
    },
    {
        label: 'Filename',
        filterId: 'filterFilename',
        filterType: 'String',
        columnId: 'Filename',
    },
];

@inject('rootStore')
@observer
class DocumentsPage extends React.Component<IDocumentsProps, IDocumentsState> {
    private presenter: DataTablePresenter<ProviderDocument>;

    private get injected(): IInjectedRootStore {
        return this.props as IInjectedRootStore;
    }

    private get claimStore(): ClaimStore {
        return this.injected.rootStore.claimStore;
    }

    constructor(props: IDocumentsProps) {
        super(props);
        this.state = {
            loading: false,
            options: undefined,
        };
        this.presenter = new DataTablePresenter<ProviderDocument>({
            name: 'documents',
            data: [],
            columns: [
                ...columns,
                {
                    id: 'Actions',
                    Header: '',
                    // accessor: 'documentType',
                    disableSortBy: true,
                    Cell: ({ cell }) =>
                        cell.row.original.id ? (
                            <DownloadCell id={cell.row.original.id} claimStore={this.claimStore} />
                        ) : null,
                },
            ],
            filterSchemas,
            filters: defaultDocumentFilters,
            filterParser: documentsFilterParser,
        });
    }

    public componentDidMount(): void {
        document.title = 'ENTER Platform - Documents';
    }
    public componentWillUnmount(): void {
        this.authContext.cancelGroup('documents');
    }

    public render() {
        return (
            <PageLayout>
                <PageHeader title="Documents" hideDateRange={true} />
                <PageLayoutRow>
                    <S.ActionBar>
                        <BaseButton variant="primary" onClick={this.handleUploadClick}>
                            <S.ButtonIcon path={mdiUpload} size={0.9} />
                            Upload Document
                        </BaseButton>
                    </S.ActionBar>
                </PageLayoutRow>
                <PageLayoutRow expand={true}>
                    <DataTableCard size="large">
                        <DataTable
                            presenter={this.presenter}
                            isLoading={this.state.loading}
                            onFetch={this.onFetch}
                            onRowClick={this.onRowClick}
                            renderRowSubComponent={this.renderRowSubComponent}
                            showExport={true}
                        />
                    </DataTableCard>
                </PageLayoutRow>
            </PageLayout>
        );
    }

    private get authContext(): IAuthContextProps {
        return this.props.authContext;
    }

    private get dialogContext(): IDialogContextProps {
        return this.props.dialogContext;
    }

    private handleUploadClick = (): void => {
        const handleClose = (): void => {
            this.dialogContext.hideDialog();
        };

        this.dialogContext.showDialog({
            props: {
                onClose: handleClose,
                renderTitle: 'Upload Document',
            },
            Component: <DocumentUploadDialog onClose={handleClose} onUploaded={this.handleUploadSuccess} />,
        });
    };

    private handleUploadSuccess = async (): Promise<void> => {
        await this.refreshListing();
    };

    private async refreshListing(): Promise<void> {
        const options = this.state.options || this.getDefaultFetchOptions();
        await this.onFetch(options);
    }

    private getDefaultFetchOptions(): ITableFetchOptions {
        const defaults = this.presenter.getTableDefaults();
        const defaultSortColumn = this.presenter.columns.find(column => !!column.defaultSort);
        const defaultSort = defaultSortColumn?.defaultSort ? [defaultSortColumn.defaultSort] : [];

        return {
            pageIndex: defaults?.pageIndex ?? 0,
            pageSize: defaults?.pageSize ?? this.presenter.controlledPageSize ?? 25,
            sortBy: _.isEmpty(defaults?.sortBy) ? defaultSort : defaults?.sortBy ?? [],
            validFilters: this.presenter.validFilters || [],
        };
    }

    private onRowClick = async (row: Row<ProviderDocument>): Promise<void> => {
        // retrieve mail cart if necessary
        row.toggleRowExpanded();
    };

    private renderRowSubComponent = (row: Row<ProviderDocument>): React.ReactElement => {
        return (
            <S.Expand>
                <DocumentDetail 
                    document={row.original} 
                    appType="provider"
                    onLoadDocumentUrl={async (documentId, documentType) => {
                        return await this.claimStore.loadDocumentDownloadUrl(documentId, documentType as ProviderDisplayDocumentType);
                    }}
                />
            </S.Expand>
        );
    };

    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        try {
            this.setState({
                options: fetchOptions,
                loading: true,
            });
            this.authContext.cancelGroup('documents');
            this.authContext.setGroup('documents');

            const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
            const sortAndOrder = parseSortBy(sortBy, this.presenter.columns);
            const listing = await this.props.rootStore.documentStore.loadDocumentListing({
                page: pageIndex,
                pageSize,
                ...sortAndOrder,
                filters: validFilters,
            });
            this.presenter.onDataUpdate(listing.items || []);
            this.presenter.onDataTotalChange(listing.total || 0);
            this.setState({
                loading: false,
            });
        } catch (err) {
            this.dialogContext.showError(err);
        }
    };
}

export const Documents: React.FC<IObservedRootStore> = inject('rootStore')(
    observer((props: IObservedRootStore) => {
        const { rootStore } = props;
        if (!rootStore) {
            return <></>;
        }
        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        return <DocumentsPage authContext={authContext} dialogContext={dialogContext} rootStore={rootStore} />;
    }),
);

export const DownloadCell: React.FunctionComponent<{ id: string; claimStore: ClaimStore }> = ({ id, claimStore }) => {
    const [loading, setLoading] = React.useState(false);

    const handleDownload = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setLoading(true);
        try {
            const docUrl = await claimStore.loadDocumentDownloadUrl(id);
            if (docUrl && docUrl.url) {
                window.open(docUrl.url, '_blank');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <S.CellAlignedRight>
            <BaseButton variant="primary" onClick={handleDownload} disabled={loading}>
                <span>Download</span>
            </BaseButton>
        </S.CellAlignedRight>
    );
};
